/**
 * Clears OIDC callback params from the URL after AuthProvider processes them.
 * Required so stale code/state in the URL does not break later auth flows.
 */
export const onSigninCallback = () => {
  window.history.replaceState({}, document.title, window.location.pathname);
};

/** sessionStorage flag: top-level prompt=none SSO check (not an interactive login). */
export const PINGFED_PROMPT_NONE_KEY = "pg-pingfed-prompt-none";

/** sessionStorage: path (+ search) to restore after PingFed redirect. */
export const PINGFED_RETURN_TO_KEY = "pg-pingfed-return-to";

const RETURN_TO_BLOCKLIST = new Set([
  "/authentication/sign-in",
  "/authentication/sign-up",
  "/authentication/reset-password",
  "/authentication/verify-email",
  "/auth2/callback",
  "/auth2/logout",
  "/access-denied",
]);

const normalizeReturnTo = (value) => {
  if (!value || typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed.startsWith("/") || trimmed.startsWith("//")) return null;
  const path = trimmed.split("?")[0].split("#")[0];
  if (!path || RETURN_TO_BLOCKLIST.has(path)) return null;
  return trimmed;
};

export const savePingfedReturnTo = (pathWithSearch) => {
  const normalized = normalizeReturnTo(pathWithSearch);
  if (!normalized) return;
  sessionStorage.setItem(PINGFED_RETURN_TO_KEY, normalized);
};

export const peekPingfedReturnTo = () =>
  normalizeReturnTo(sessionStorage.getItem(PINGFED_RETURN_TO_KEY));

export const consumePingfedReturnTo = (fallback = "/") => {
  const saved = peekPingfedReturnTo();
  sessionStorage.removeItem(PINGFED_RETURN_TO_KEY);
  return saved || fallback;
};

/**
 * Build navigate() args for username/password sign-in after a PingFed failure.
 * Keeps the deep-link return path so password login (or a later PingFed retry)
 * can restore it.
 */
export const passwordSignInNavigateArgs = () => {
  sessionStorage.removeItem(PINGFED_PROMPT_NONE_KEY);
  const returnTo = peekPingfedReturnTo();
  return {
    replace: true,
    state: returnTo
      ? {
          from: {
            pathname: returnTo.split("?")[0],
            search: returnTo.includes("?")
              ? `?${returnTo.split("?").slice(1).join("?")}`
              : "",
          },
        }
      : undefined,
  };
};

const redirectUri = `${window.location.origin}/auth2/callback`;

export const oidcConfig = {
  authority: process.env.REACT_APP_PINGFED_AUTHORITY || "https://fedauth.pg.com",
  client_id: process.env.REACT_APP_PINGFED_CLIENT_ID,
  redirect_uri: redirectUri,
  // Reuse the already-registered callback URI (same-tab automaticSilentRenew iframe)
  silent_redirect_uri: redirectUri,
  post_logout_redirect_uri: `${window.location.origin}/auth2/logout`,
  response_type: "code",
  scope: "openid profile email",
  automaticSilentRenew: true,
  silentRequestTimeoutInSeconds: 20,
  onSigninCallback,
};
